import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isEmail, normalizeKzPhone, type PackDto, type PayMethod, type ProfileResponse } from '@app/shared';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { creditsCount } from '../lib/format';
import {
  methodRows,
  nextState,
  pollVerdict,
  POLL_GIVE_UP_MS,
  POLL_MS,
  rememberedKaspiPhone,
  rememberKaspiPhone,
  type SheetState,
} from '../lib/purchase';
import { haptic, openExternalLink, openInvoice, requestPhone } from '../lib/tma';
import { BottomSheet, Button, Spinner } from './ui';

const inputClass =
  'w-full rounded-lg px-4 py-2.5 text-sm bg-surface-2 border-0 focus:outline-none focus:ring-2 focus:ring-ink/70';

export interface PurchaseSheetProps {
  /** The pack being bought; null = the sheet is closed. */
  pack: PackDto | null;
  /** The e-mail typed for lava.top last time. */
  email: string | null;
  offerUrl: string;
  onClose(): void;
}

/** «Как оплатить» (spec 3.2–3.5): a method, its one question, then waiting until the money is in. */
export function PurchaseSheet({ pack, email, offerUrl, onClose }: PurchaseSheetProps) {
  const t = useT();
  const p = t.purchase;
  const qc = useQueryClient();
  const [state, setState] = useState<SheetState>({ step: 'methods' });
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState(rememberedKaspiPhone);
  const [mail, setMail] = useState(email ?? '');
  const open = pack !== null;

  // Every opening starts at the methods.
  useEffect(() => {
    if (!pack) return;
    setState({ step: 'methods' });
    setMessage(null);
    setMail((m) => m || email || '');
  }, [pack, email]);

  async function start(method: PayMethod, extra: { phone?: string; email?: string } = {}) {
    if (!pack || busy) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await api.startPurchase({ packId: pack.id, method, ...extra });
      const next = nextState(state, res, { method, t });
      if (res.kind === 'kaspi_sent' && extra.phone) rememberKaspiPhone(extra.phone);
      if (res.kind === 'lava_link') openExternalLink(res.url);
      if (res.kind === 'unavailable') void qc.invalidateQueries({ queryKey: ['payment-options'] });
      setState(next.state);
      setMessage(next.message ?? null);
      if (res.kind === 'stars_link') {
        // The window's «paid» is only a hint: the credits come with the bot's confirmation.
        const status = await openInvoice(res.url);
        if (status === 'paid') setMessage(p.starsPaid);
        else if (status === 'cancelled') setState({ step: 'methods' });
        else if (status === 'failed') {
          setState({ step: 'methods' });
          setMessage(p.failed);
        }
      }
    } catch {
      setMessage(p.error);
    } finally {
      setBusy(false);
    }
  }

  function choose(method: PayMethod) {
    setMessage(null);
    if (method === 'kaspi') setState({ step: 'kaspi' });
    else if (method === 'stars') void start('stars');
    else setState({ step: 'email', method });
  }

  function sendKaspi() {
    if (!normalizeKzPhone(phone)) {
      setMessage(p.badPhone);
      return;
    }
    void start('kaspi', { phone });
  }

  function sendLava(method: PayMethod) {
    if (!isEmail(mail)) {
      setMessage(p.badEmail);
      return;
    }
    void start(method, { email: mail.trim() });
  }

  async function takeFromTelegram() {
    const got = await requestPhone();
    if (got) setPhone(got.startsWith('+') ? got : `+${got}`);
  }

  // Waiting: ask about the order every 3 s while the sheet is open, for at most 30 minutes.
  const waiting = state.step === 'waiting' ? state : null;
  useEffect(() => {
    if (!open || !waiting) return;
    const started = Date.now();
    let stopped = false;
    const id = setInterval(async () => {
      if (stopped) return;
      if (Date.now() - started > POLL_GIVE_UP_MS) {
        stopped = true;
        clearInterval(id);
        setMessage(p.gaveUp);
        return;
      }
      try {
        const s = await api.getOrderStatus(waiting.code);
        if (stopped) return;
        const verdict = pollVerdict(waiting, s.status);
        if (verdict === 'wait') return;
        stopped = true;
        clearInterval(id);
        if (verdict === 'paid') {
          haptic('success');
          setState({ step: 'paid', code: s.code, credits: s.credits, balance: s.balance });
          setMessage(null);
          void qc.invalidateQueries({ queryKey: ['credits'] });
          api
            .getProfile()
            .then((profile) => qc.setQueryData<ProfileResponse>(['profile'], profile))
            .catch(() => {});
        } else {
          setState({ step: 'methods' });
          setMessage(verdict === 'failed' ? p.failed : p.expired);
        }
      } catch {
        // A lost poll is asked again in 3 s.
      }
    }, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(id);
    };
    // One polling per order: the waiting state is identified by its code.
  }, [open, waiting?.code]);

  const offerHref = new URL(offerUrl, window.location.href).toString();

  return (
    <BottomSheet open={open} onClose={onClose} title={pack ? `${p.sheetTitle} · ${pack.title}` : p.sheetTitle}>
      {pack && (
        <div className="space-y-3 pb-1">
          {state.step === 'methods' && (
            <>
              <ul className="divide-y divide-line rounded-xl bg-surface-2">
                {methodRows(pack, t).map((row) => (
                  <li key={row.method}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => choose(row.method)}
                      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm disabled:opacity-50"
                    >
                      <span className="font-semibold">{row.label}</span>
                      <span className="tabular-nums whitespace-nowrap">{row.price}</span>
                    </button>
                  </li>
                ))}
              </ul>
              {busy && <Spinner size="sm" ariaLabel={t.common.loading} />}
            </>
          )}

          {state.step === 'kaspi' && (
            <>
              <label className="block text-xs text-hint">
                {p.phoneLabel}
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={phone}
                  placeholder={p.phonePlaceholder}
                  onChange={(e) => setPhone(e.target.value)}
                  className={`${inputClass} mt-1`}
                />
              </label>
              <Button variant="secondary" size="sm" onClick={() => void takeFromTelegram()} disabled={busy}>
                {p.fromTelegram}
              </Button>
              <Button variant="primary" size="lg" fullWidth loading={busy} disabled={busy} onClick={sendKaspi}>
                {p.sendInvoice}
              </Button>
            </>
          )}

          {state.step === 'email' && (
            <>
              <label className="block text-xs text-hint">
                {p.emailLabel}
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={mail}
                  placeholder={p.emailPlaceholder}
                  onChange={(e) => setMail(e.target.value)}
                  className={`${inputClass} mt-1`}
                />
              </label>
              <Button variant="primary" size="lg" fullWidth loading={busy} disabled={busy} onClick={() => sendLava(state.method)}>
                {p.toPayment}
              </Button>
            </>
          )}

          {state.step === 'waiting' && (
            <WaitingPart
              state={state}
              busy={busy}
              onNewLink={() => void start(state.method, { email: mail.trim() })}
            />
          )}

          {state.step === 'paid' && (
            <div className="space-y-2 text-center py-2">
              <p className="text-lg font-bold">{p.paid(creditsCount(state.credits))}</p>
              {state.balance !== null && <p className="text-sm text-hint">{p.paidBalance(state.balance)}</p>}
              <p className="text-xs text-hint">{p.orderLine(state.code)}</p>
              <Button variant="primary" size="lg" fullWidth onClick={onClose}>
                {p.done}
              </Button>
            </div>
          )}

          {message && <p className="text-sm text-warning">{message}</p>}

          {(state.step === 'kaspi' || state.step === 'email' || state.step === 'waiting') && (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => {
                setState({ step: 'methods' });
                setMessage(null);
              }}
            >
              {p.otherMethod}
            </Button>
          )}

          {state.step !== 'paid' && (
            <p className="text-xs text-hint">
              {p.offerBefore}
              <a
                href={offerHref}
                className="underline"
                onClick={(e) => {
                  e.preventDefault();
                  openExternalLink(offerHref);
                }}
              >
                {p.offerLink}
              </a>
            </p>
          )}
        </div>
      )}
    </BottomSheet>
  );
}

function WaitingPart({
  state,
  busy,
  onNewLink,
}: {
  state: Extract<SheetState, { step: 'waiting' }>;
  busy: boolean;
  onNewLink(): void;
}) {
  const t = useT();
  const p = t.purchase;
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3">
        <Spinner size="sm" ariaLabel={t.common.loading} />
        <p className="text-sm">
          {state.method === 'kaspi'
            ? state.mask && state.amount
              ? p.waitingKaspi(state.amount, state.mask)
              : p.waitingLava
            : state.method === 'stars'
              ? p.starsWaiting
              : p.waitingLava}
        </p>
      </div>
      <p className="text-xs text-hint">{p.waitingHint}</p>
      <p className="text-xs text-hint">{p.orderLine(state.code)}</p>
      {state.url && (
        // A direct tap is always a user gesture: Telegram opens the page even when the first open was dropped.
        <Button variant="secondary" size="sm" onClick={() => openExternalLink(state.url!)}>
          {p.openLink}
        </Button>
      )}
      {state.method === 'lava_usd' && (
        <>
          <p className="text-xs text-hint">{p.usdLife}</p>
          <Button variant="secondary" size="sm" disabled={busy} onClick={onNewLink}>
            {p.newLink}
          </Button>
        </>
      )}
    </div>
  );
}
